import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Tag, Search, Scan, Printer, Settings, Cloud } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useMasterProductSearch } from '@/hooks/useMasterProductSearch';
import { useScanditScanner } from '@/hooks/useScanditScanner';
import { useLabelSettings } from '@/contexts/LabelSettingsContext';
import { useHtmlTemplateEngine } from '@/hooks/useHtmlTemplateEngine';
import { useSimplifiedPrinting } from '@/hooks/useSimplifiedPrinting';
import { usePrintNode } from '@/hooks/usePrintNode';
import { PrinterSelector } from './PrinterSelector';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface StoreStationLabelsProps {
  onBack: () => void;
}

export const StoreStationLabels = ({ onBack }: StoreStationLabelsProps) => {
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [autoPrint, setAutoPrint] = useState(() => {
    const saved = localStorage.getItem('autoPrint');
    return saved ? JSON.parse(saved) : false;
  });
  const [apiPrint, setApiPrint] = useState(() => {
    const saved = localStorage.getItem('apiPrint');
    return saved ? JSON.parse(saved) : false;
  });
  const [printFormat, setPrintFormat] = useState<'html' | 'pdf'>('pdf');
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { dimensions } = useLabelSettings();
  const { defaultTemplate: htmlTemplate, generateHtmlLabel, loading: templateLoading } = useHtmlTemplateEngine();
  const { printLabel, browserPrint, loading: printLoading } = useSimplifiedPrinting();
  const { selectedPrinter } = usePrintNode();
  
  const { 
    searchTerm, 
    setSearchTerm, 
    searchResults, 
    isLoading: searchLoading, 
    searchByBarcode 
  } = useMasterProductSearch();

  const handleScanResult = useCallback(async (barcode: string) => {
    console.log('Barcode scanned:', barcode);
    setShowScanner(false);
    
    try {
      const product = await searchByBarcode(barcode);
      if (product) {
        setSelectedProduct(product);
        toast({
          title: "Product Found",
          description: `Found: ${product.product_name}`,
        });
      } else {
        toast({
          title: "Product Not Found",
          description: "No product found with this barcode",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error searching by barcode:', error);
      toast({
        title: "Search Error",
        description: "Failed to search for product",
        variant: "destructive"
      });
    }
  }, [searchByBarcode, toast]);

  const handleScanClose = useCallback(() => {
    setShowScanner(false);
  }, []);

  const scannerProps = useScanditScanner({
    onScanResult: handleScanResult,
    onClose: handleScanClose
  });

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('apiPrint', JSON.stringify(apiPrint));
  }, [apiPrint]);

  useEffect(() => {
    localStorage.setItem('autoPrint', JSON.stringify(autoPrint));
  }, [autoPrint]);

  useEffect(() => {
    if (inputRef.current && !selectedProduct) {
      inputRef.current.focus();
    }
  }, [selectedProduct]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (selectedProduct && /^[a-zA-Z0-9 ]$/.test(event.key)) {
        event.preventDefault();
        setSelectedProduct(null);
        setSearchTerm(event.key);
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(1, 1);
          }
        }, 50);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedProduct, setSearchTerm]);

  const handleProductSelect = (product: any) => {
    setSelectedProduct(product);
    setSearchTerm("");
    toast({
      title: "Product Selected",
      description: `Selected: ${product.product_name}`,
    });
    
    if (autoPrint) {
      setTimeout(() => handlePrintLabel(product), 500);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && searchResults.length > 0) {
      event.preventDefault();
      handleProductSelect(searchResults[0]);
    }
  };

  const handleApiPrintLabel = async (product = selectedProduct) => {
    if (!product || !htmlTemplate) return;
    
    if (!selectedPrinter) {
      toast({
        title: "No Printer Selected",
        description: "Please select a printer to use API printing",
        variant: "destructive"
      });
      return;
    }
    
    console.log('🖨️ Simplified API printing with HTML template:', htmlTemplate.name);
    
    try {
      const success = await printLabel(
        htmlTemplate,
        {
          barcode: product.barcode,
          product_name: product.product_name,
          retail_price: product.retail_price,
          master_categories: product.master_categories
        },
        selectedPrinter.id,
        printFormat
      );
      
      if (success) {
        console.log('✅ Simplified print job completed successfully');
      }
    } catch (error) {
      console.error('❌ Simplified API print failed:', error);
      handleBrowserPrint(product);
    }
  };
  
  const handleBrowserPrint = (product = selectedProduct) => {
    if (!product || !htmlTemplate) return;
    
    console.log('🖨️ Simplified browser printing with HTML template:', htmlTemplate.name);
    
    browserPrint(htmlTemplate, {
      barcode: product.barcode,
      product_name: product.product_name,
      retail_price: product.retail_price,
      master_categories: product.master_categories
    });
  };

  const handlePrintLabel = (product = selectedProduct) => {
    if (!product || !htmlTemplate) return;
    
    if (apiPrint) {
      handleApiPrintLabel(product);
      return;
    }
    
    handleBrowserPrint(product);
  };

  const startNewSearch = () => {
    setSelectedProduct(null);
    setSearchTerm("");
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  // Calculate preview dimensions
  const previewDimensions = useMemo(() => {
    if (!htmlTemplate?.dimensions) {
      const widthPx = dimensions.unit === 'in' ? dimensions.width * 96 : dimensions.width * 3.78;
      const heightPx = dimensions.unit === 'in' ? dimensions.height * 96 : dimensions.height * 3.78;
      return { width: Math.max(widthPx, 128), height: Math.max(heightPx, 80) };
    }
    
    const { width, height, unit } = htmlTemplate.dimensions;
    const widthPx = unit === 'in' ? width * 96 : (width / 25.4) * 96;
    const heightPx = unit === 'in' ? height * 96 : (height / 25.4) * 96;
    
    const maxWidth = 300;
    const maxHeight = 200;
    const scaleX = maxWidth / widthPx;
    const scaleY = maxHeight / heightPx;
    const scale = Math.min(scaleX, scaleY, 1);
    
    return { 
      width: Math.max(widthPx * scale, 128), 
      height: Math.max(heightPx * scale, 80) 
    };
  }, [htmlTemplate, dimensions]);

  if (showScanner) {
    return (
      <BarcodeScanner
        onScanResult={handleScanResult}
        onClose={() => setShowScanner(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white p-6 flex flex-col">
      <div className="flex items-center justify-between mb-8">
        <Button 
          onClick={onBack} 
          variant="outline"
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Store Station
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">Product Labels</h1>
        <div></div>
      </div>

      {!selectedProduct ? (
        <div className="flex-1 flex items-center justify-center relative">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 justify-center">
                <Search className="w-5 h-5" />
                Find Product
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                ref={inputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type product name or scan barcode..."
                className="text-lg p-4"
                autoFocus
              />
              
              <Button
                onClick={() => setShowScanner(true)}
                variant="outline"
                className="w-full"
              >
                <Scan className="w-5 h-5 mr-2" />
                Open Scanner
              </Button>
              
              {searchLoading && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {searchResults.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <div className="font-medium">{product.product_name}</div>
                      <div className="text-sm text-gray-600">
                        ${product.retail_price?.toFixed(2) || '0.00'} • {product.barcode || 'No barcode'}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="absolute bottom-4 right-4">
                <div className="bg-white border rounded-lg p-3 shadow-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Cloud className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium">API Print</span>
                    <Switch
                      checked={apiPrint}
                      onCheckedChange={setApiPrint}
                    />
                  </div>
                  
                  {apiPrint && (
                    <div className="border-t pt-3">
                      <PrinterSelector className="w-48" />
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium">Auto Print</span>
                    <Switch
                      checked={autoPrint}
                      onCheckedChange={setAutoPrint}
                    />
                  </div>
                  
                  {apiPrint && (
                    <div className="flex items-center gap-2">
                      <Printer className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium">Format:</span>
                      <Select value={printFormat} onValueChange={(value: 'html' | 'pdf') => setPrintFormat(value)}>
                        <SelectTrigger className="w-16 h-6 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="html">HTML</SelectItem>
                          <SelectItem value="pdf">PDF</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <Card className="w-full max-w-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Label Preview
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={startNewSearch}
                    variant="outline"
                    size="sm"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Search Another
                  </Button>
                  <Button
                    onClick={() => handlePrintLabel()}
                    disabled={templateLoading || printLoading}
                    size="sm"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print Label
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{selectedProduct.product_name}</div>
                      <div className="text-sm text-gray-600">
                        ${selectedProduct.retail_price?.toFixed(2) || '0.00'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Barcode</div>
                      <div className="font-mono text-sm">{selectedProduct.barcode || 'None'}</div>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-white">
                  <div className="mb-2 text-sm text-gray-600 text-center">
                    HTML Label Preview ({previewDimensions.width.toFixed(0)} × {previewDimensions.height.toFixed(0)}px)
                  </div>
                  <div className="flex justify-center">
                    <div 
                      style={{
                        width: `${previewDimensions.width}px`,
                        height: `${previewDimensions.height}px`,
                        border: '2px solid #e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                        transform: 'scale(0.8)',
                        transformOrigin: 'center'
                      }}
                      dangerouslySetInnerHTML={{
                        __html: htmlTemplate ? generateHtmlLabel(htmlTemplate, {
                          barcode: selectedProduct.barcode,
                          product_name: selectedProduct.product_name,
                          retail_price: selectedProduct.retail_price,
                          master_categories: selectedProduct.master_categories
                        }).replace('<!DOCTYPE html><html><head>', '').replace(/.*<\/head><body>/, '').replace('</body></html>', '') : '<div>No template loaded</div>'
                      }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};